import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";
import PaymentsPage from "./PaymentsPage";
import { CashPositionTab, VendorPaymentsTab, ExpensesTab } from "./payments/LedgerTabs";
import ReceivablesTab from "./payments/ReceivablesTab";

/**
 * Payments hub: one place for money in, money out, and the net position.
 * Tabs appear according to the user's role (see lib/permissions.js).
 */
export default function PaymentsHubPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tabs = useMemo(() => [
    { key: "cash", label: "Cash Position", show: can(user, "cash_position.view") },
    { key: "customer", label: "Customer Payments", show: can(user, "client_payments.view") },
    { key: "receivables", label: "Receivables", show: can(user, "client_payments.view") },
    { key: "vendor", label: "Vendor Payments", show: can(user, "vendor_payments.view") },
    { key: "expenses", label: "Expenses", show: can(user, "expenses.view") },
  ].filter(t => t.show), [user]);
  const requested = params.get("tab");
  const [tab, setTab] = useState(tabs.find(t => t.key === requested)?.key || tabs[0]?.key);
  useEffect(() => { if (requested && tabs.some(t => t.key === requested)) setTab(requested); }, [requested, tabs]);
  const select = (k) => { setTab(k); setParams({ tab: k }, { replace: true }); };

  if (!tabs.length) return null;
  return (
    <div className="animate-fade-up">
      <div className="px-4 sm:px-6 pt-6 pb-0 flex items-center gap-1 bg-[#F1F5F9]/60 border-b border-[#E2E8F0] overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => select(t.key)} data-testid={`payments-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${tab === t.key ? "bg-white text-[#0A1128] border-[#FFB703]" : "text-[#64748B] border-transparent hover:text-[#0A1128]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "customer" ? <PaymentsPage embedded /> : (
        <div className="p-6 max-w-6xl">
          {tab === "cash" && <CashPositionTab />}
          {tab === "receivables" && <ReceivablesTab />}
          {tab === "vendor" && <VendorPaymentsTab />}
          {tab === "expenses" && <ExpensesTab />}
        </div>
      )}
    </div>
  );
}
